"use client";

import { useCallback, useEffect, useState } from "react";
import { AdminAuthError, adminFetch } from "@/lib/admin-client";
import { AdminKeyGate } from "@/components/admin-key-gate";
import { SchemaEditor } from "@/components/schema-editor";
import { Badge, Button, Card, Spinner } from "@/components/ui";

interface Twin {
  id: string;
  title: string;
  brand?: string;
  price?: string;
  category?: string;
  sellingPoints: string[];
  images: string[];
  sourceUrl?: string;
  source: "scrape" | "manual";
  createdAt: string;
}

export default function AdminProductsPage() {
  const [items, setItems] = useState<Twin[] | null>(null);
  const [needKey, setNeedKey] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await adminFetch<Twin[]>("/api/admin/assets/products"));
      setNeedKey(false);
    } catch (e) {
      if (e instanceof AdminAuthError) setNeedKey(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(t: Twin) {
    if (!window.confirm(`Delete product twin "${t.title}"?`)) return;
    setBusy(t.id);
    try {
      await adminFetch(`/api/admin/assets/products?id=${t.id}`, {
        method: "DELETE",
      });
      await load();
    } finally {
      setBusy(null);
    }
  }

  if (needKey) return <AdminKeyGate onUnlock={() => void load()} />;
  if (!items) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:py-10">
      <h1 className="text-2xl font-semibold tracking-tight">
        Product Digital Twin container
      </h1>
      <p className="mt-2 text-sm text-muted">
        The container defines the recording format — which elements every
        product twin must capture, per tech route. User data is stored as
        instances of this format.
      </p>

      <SchemaEditor kind="product" />

      <h2 className="mt-10 mb-3 text-sm font-medium text-muted">
        Captured instances (auto-registered by wizard scrapes)
      </h2>

      {items.length === 0 && (
        <Card className="mt-8 px-6 py-16 text-center text-muted">
          No product twins yet — run a scrape in the studio wizard.
        </Card>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {items.map((t) => (
          <Card key={t.id} className="flex gap-4 p-4">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={t.images[0]}
              alt=""
              className="size-20 shrink-0 rounded-xl object-cover"
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{t.title}</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                <Badge>{t.source}</Badge>
                {t.price && <Badge tone="accent">{t.price}</Badge>}
                {t.category && <Badge>{t.category}</Badge>}
              </div>
              <div className="mt-2 line-clamp-2 text-[12px] text-muted">
                {t.sellingPoints.join(" · ")}
              </div>
              <div className="mt-2.5 flex items-center justify-between">
                <span className="font-mono text-[11px] text-muted">
                  {t.id}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  className="text-[var(--danger)]"
                  disabled={busy === t.id}
                  onClick={() => void remove(t)}
                >
                  Delete
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
